import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import { ProfileExtrusionDescriptorType } from "building-types/profiles/profileTypesEnum";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { RoofDormerWallBaseBoardRails } from "building-types/roofs/roofGenerationTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { WallRoofBaseBoardMode, WallRoofBaseBoardFilter } from "building-types/settings/settingsTypesEnum";
import { ExteriorTrimViewNoteType } from "building-types/settings/viewNotesSettings";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { defaultAllExteriorTrimViewNotes } from "doc/settings/defaultViewNoteSettings";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { viewEntityDescriptorNew, faceArrayViewEntityNew } from "doc/utils/viewEntityTools";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import { isExteriorWallCondition } from "doc/utils/wallTypeTools";
import { aabb2ByTwoPoints, aabb2Grow } from "geom/aabb2";
import { aabb2Width } from "geom/aabb2Types";
import { aabb3ByTwoPoints } from "geom/aabb3";
import type { Face } from "geom/brep";
import { faceNew, tagFaces } from "geom/brep";
import {
  addConvexPlanesToBSPLeaf,
  addPolygonsToBSP,
  buildEdgePolygon,
  buildPolygonsFromPolyline2,
  isPointInsideBSP,
  rayIntersectionBSP,
} from "geom/bsp";
import type { ClipFaceContext, ClipRailContext } from "geom/bspClipping";
import { clipFaces, clipRail3, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PlaneBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { extrudeFaceAlongPolyLine3, extrudeFaceAlongPolyLine3OnPlane } from "geom/extrude";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { offsetPolyLine2, offsetPolyLine3InXYPlane } from "geom/offset";
import {
  planeByThreePoints,
  planeCopyMoved,
  planeCopyReversed,
  planeIsCoplanar,
  planeMoveAlongNormal,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProject3,
  planeProjectPolyLine3,
  planeReProjectPolyLine2,
  polyLine2OnPlane,
} from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2Rotate,
  polyLine2ScaleY,
  polyLine2SetCW,
  polyLine2Simplify,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3Concat,
  polyLine3Copy,
  polyLine3Edge,
  polyLine3EdgeCount,
  polyLine3EdgeEnd,
  polyLine3EdgeStart,
  polyLine3EdgeTangent,
  polyLine3EndPoint,
  polyLine3Move,
  polyLine3New,
  polyLine3OnPlane,
  polyLine3Simplify,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { pushXYZ } from "geom/utils";
import { vec2Dot } from "geom/vec2";
import { vec3AngleBetween, vec3CrossNormalize, vec3Normalize } from "geom/vec3";
import RBush from "rbush";
import { RBush3D } from "rbush-3d";
import { getSoffitDepth } from "../elements/roofTrimUtility";
import { pushMissingProfileReferenceIssue } from "./profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "./profileTools";
import type { CoplanarRails } from "./roofs/roofRails";
import {
  coplanarRailsNew,
  groupCoplanarRails,
  rejoinCoplanarRails,
  reprojectCoplanarRailsArrayToWalls,
} from "./roofs/roofRails";
import { getWallPath } from "./walls/wall";

const WALL_BASE_BOARD_COPLANAR_TOLERANCE = 0.01;
const HORIZONTAL_WALL_BASE_BOARD_TOLERANCE = 0.01;
const WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH = 0.1;

interface BuildSWallBaseBoardsOptions {
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly roofTopFaces: RoofFace[];
  readonly profileSource: ActiveProfileSource;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly clipContext: MultiGableRoofClipContext;
  readonly walls: Table<WallSystem>;
  readonly isDormer: boolean;
  readonly issues?: BuildingGenerationIssue[];
}

export function buildWallBaseBoards({
  subRoof,
  subRoofs,
  roofTopFaces,
  profileSource,
  exteriorTrimSettings,
  clipContext,
  walls,
  isDormer,
  issues,
}: BuildSWallBaseBoardsOptions): { wallBaseBoards: FaceArrayViewEntity[]; wallBaseBoardRails: PolyLine3[] } {
  const wallBaseBoards: FaceArrayViewEntity[] = [];

  // dormers go through a different path
  if (isDormer) {
    return { wallBaseBoards, wallBaseBoardRails: [] };
  }

  const { wallRoofBaseBoard, wallRoofBaseBoardProfile, pickHandle } = subRoof;

  if (wallRoofBaseBoard === WallRoofBaseBoardMode.NONE) {
    return { wallBaseBoards, wallBaseBoardRails: [] };
  }

  const tag: BuildingGeomTag[] = [
    {
      type: "PARENT",
      parentId: subRoof.id,
      parentType: subRoof.roofType,
    },
    {
      type: "CONDITION",
      condition: "WALL_BASE_BOARD",
    },
  ];

  // prepare the board profile
  const descriptor = getActiveProfileDescriptor(
    profileSource,
    wallRoofBaseBoardProfile,
    DEFAULT_TRIM_PROFILE_DESCRIPTOR,
  );
  if (!descriptor) {
    pushMissingProfileReferenceIssue(
      issues,
      profileSource,
      wallRoofBaseBoardProfile,
      pickHandle ?? (exteriorTrimSettings.id ? { type: "SETTINGS" as const, id: exteriorTrimSettings.id } : undefined),
      "wallRoofBaseBoardProfile",
    );
    return { wallBaseBoards, wallBaseBoardRails: [] };
  }
  let baseBoardProfile = polyLine2Copy(getProfileExtrusionDescriptorProfile(descriptor));
  baseBoardProfile = polyLine2Simplify(baseBoardProfile);
  let capProfile = polyLine2Copy(baseBoardProfile);

  const baseBoardProfileAABB = polyLine2AABB2(baseBoardProfile);

  if (descriptor.profileType === ProfileExtrusionDescriptorType.CUSTOM && descriptor.reflectOverY) {
    baseBoardProfile = polyLine2Move(baseBoardProfile, baseBoardProfileAABB.minX, -baseBoardProfileAABB.maxY);
    capProfile = polyLine2Move(capProfile, baseBoardProfileAABB.minX, -baseBoardProfileAABB.maxY);
  } else {
    baseBoardProfile = polyLine2Move(baseBoardProfile, baseBoardProfileAABB.minX, -baseBoardProfileAABB.minY);
    capProfile = polyLine2Move(capProfile, baseBoardProfileAABB.minX, -baseBoardProfileAABB.minY);
  }

  const finalWallBaseBoardRails: PolyLine3[] = [];

  // build the rails
  const { wallBaseBoardRails, rtree } = buildWallBaseBoardRails({
    subRoof,
    subRoofs,
    roofTopFaces,
    walls,
    clipContext,
  });

  // generate boards from rails
  let index = 0;
  for (const { rails, plane } of wallBaseBoardRails) {
    const { uX, uY, uZ } = plane;
    const planeForExtrusion = planeCopyReversed(plane);

    // prepare the clipping for this coplanar rails
    const wallBaseBoardClippingBSPs = buildWallBaseBoardClippingBSPs(
      walls,
      subRoofs,
      plane,
      aabb2Width(baseBoardProfileAABB),
    );

    for (const rail of rails) {
      if (!rail) {
        continue;
      }

      const [startX, startY, startZ] = polyLine3StartPoint(rail);
      const [endX, endY, endZ] = polyLine3EndPoint(rail);

      const [startTx, startTy] = polyLine3EdgeTangent(rail, 0);
      const [endTx, endTy] = polyLine3EdgeTangent(rail, polyLine3EdgeCount(rail) - 1);

      const offset = 0.1;
      let finalRail = rail;

      const inputFaces: Face[] = [];
      const extraClipPlanes: PlaneBSP[] = [];

      // check if it's connected with another rail
      const startAABB = aabb3ByTwoPoints(
        startX - WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        startY - WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        startZ - WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        startX + WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        startY + WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        startZ + WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
      );

      const endAABB = aabb3ByTwoPoints(
        endX - WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        endY - WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        endZ - WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        endX + WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        endY + WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
        endZ + WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
      );

      const isOpenStart = rtree.search(startAABB).findIndex((x) => x.index !== index) < 0;
      const isOpenEnd = rtree.search(endAABB).findIndex((x) => x.index !== index) < 0;

      // adjust rail ends
      if (
        startZ > endZ + DIST_EPSILON &&
        (wallRoofBaseBoard === WallRoofBaseBoardMode.ROOF_TOP_PLUMB_CUT || !isOpenStart)
      ) {
        finalRail = polyLine3Concat([startX - startTx * offset, startY - startTy * offset, startZ], finalRail);
      }

      if (
        endZ > startZ + DIST_EPSILON &&
        (wallRoofBaseBoard === WallRoofBaseBoardMode.ROOF_TOP_PLUMB_CUT || !isOpenEnd)
      ) {
        finalRail = polyLine3Concat(finalRail, [endX + endTx * offset, endY + endTy * offset, endZ]);
      }

      finalWallBaseBoardRails.push(finalRail);

      // prepare plumb cut planes and cap faces
      if (wallRoofBaseBoard === WallRoofBaseBoardMode.ROOF_TOP_PLUMB_CUT) {
        const [startUX, startUY, startUZ] = vec3CrossNormalize(0, 0, 1, -uX, -uY, -uZ);
        const startPlane = planeNew(startX, startY, startZ, startUX, startUY, startUZ, 0, 0, 1);
        const startEdge = polyLine3Edge(rail, 0);
        const startSlopeAngle = vec3AngleBetween(
          startEdge[3] - startEdge[0],
          startEdge[4] - startEdge[1],
          startEdge[5] - startEdge[2],
          startTx,
          startTy,
          0,
        );

        const [endUX, endUY, endUZ] = vec3CrossNormalize(0, 0, 1, uX, uY, uZ);
        const endPlane = planeNew(endX, endY, endZ, endUX, endUY, endUZ, 0, 0, 1);
        const endEdge = polyLine3Edge(rail, polyLine3EdgeCount(rail) - 1);
        const endSlopeAngle = vec3AngleBetween(
          endEdge[3] - endEdge[0],
          endEdge[4] - endEdge[1],
          endEdge[5] - endEdge[2],
          endTx,
          endTy,
          0,
        );

        const startCapProfile = polyLine2ScaleY(polyLine2Copy(capProfile), 1 / Math.cos(startSlopeAngle));
        const endCapProfile = polyLine2ScaleY(
          polyLine2Reverse(polyLine2ReflectOverYAxis(polyLine2Copy(capProfile))),
          1 / Math.cos(endSlopeAngle),
        );

        inputFaces.push(faceNew([startCapProfile], startPlane));
        inputFaces.push(faceNew([endCapProfile], endPlane));

        extraClipPlanes.push(planeBSPFromPlane(startPlane));
        extraClipPlanes.push(planeBSPFromPlane(endPlane));
      }

      // extrude
      const brep = extrudeFaceAlongPolyLine3OnPlane([baseBoardProfile], finalRail, planeForExtrusion);

      // perform clipping
      const extraClippingBSP = addConvexPlanesToBSPLeaf(extraClipPlanes);
      clipFaces({
        faces: brep.faces,
        options: {
          clippingNodes: [clippingNodeNew(extraClippingBSP, true)],
          coplanarsToFront: false,
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
        clippedFaces: inputFaces,
      });

      const clippedFaces: Face[] = [];
      for (const bsp of wallBaseBoardClippingBSPs) {
        clipFaces({
          faces: inputFaces,
          clippedFaces,
          options: {
            clippingNodes: [clippingNodeNew(bsp, true)],
            coplanarsToFront: false,
            epsilon: MULTIGABLE_BSP_EPSILON,
          },
        });
      }

      // view note descriptor
      const exteriorTrimViewNoteTable = mergeViewNoteTables(
        defaultAllExteriorTrimViewNotes,
        exteriorTrimSettings.viewNotes,
      );
      const exteriorTrimViewEntityDescriptor = viewEntityDescriptorNew(
        uid(),
        ViewEntityType.WALL_ROOF_BASEBOARD,
        exteriorTrimViewNoteTable[ExteriorTrimViewNoteType.WALL_ROOF_BASEBOARD],
      );

      // insert the trim
      wallBaseBoards.push(
        faceArrayViewEntityNew(
          tagFaces(clippedFaces, tag),
          exteriorTrimViewEntityDescriptor,
          "trimExterior0",
          pickHandle,
        ),
      );

      index++;
    }
  }

  return { wallBaseBoards, wallBaseBoardRails: finalWallBaseBoardRails };
}

interface BuildDormerWallBaseBoardsOptions {
  readonly wallBaseBoardRails?: RoofDormerWallBaseBoardRails;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly profileSource: ActiveProfileSource;
  readonly clipContext: MultiGableRoofClipContext;
  readonly wallBaseBoards: FaceArrayViewEntity[];
  readonly pickHandle: DocEntityHandle | undefined;
  readonly issues?: BuildingGenerationIssue[];
}

export function buildDormerWallBaseBoards({
  wallBaseBoardRails,
  subRoofs,
  profileSource,
  clipContext,
  wallBaseBoards,
  pickHandle,
  issues,
}: BuildDormerWallBaseBoardsOptions) {
  if (!wallBaseBoardRails) {
    return;
  }

  const { profileId, height, sections } = wallBaseBoardRails;

  const { discardBSP } = clipContext;

  const tag: BuildingGeomTag[] = [
    {
      type: "PARENT",
      parentId: profileId,
      parentType: "PROFILE",
    },
    {
      type: "CONDITION",
      condition: "DORMER_WALL_BASE_BOARDS",
    },
  ];

  const descriptor = getActiveProfileDescriptor(profileSource, profileId, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
  if (!descriptor) {
    pushMissingProfileReferenceIssue(issues, profileSource, profileId, pickHandle, "wallRoofBaseBoardProfile");
    return;
  }
  const profile = polyLine2Simplify(polyLine2Copy(getProfileExtrusionDescriptorProfile(descriptor)));
  polyLine2Rotate(profile, Math.PI);
  const profileAABB = polyLine2AABB2(profile);
  const thickness = aabb2Width(profileAABB);

  polyLine2Move(profile, -profileAABB.maxX, -profileAABB.minY);

  const basePlane = planeNewXY(0, 0, height);

  for (const { segments, clipPlane, startCapPlane, endCapPlane } of sections) {
    const wallBaseBoardRails = projectDormerSegments({
      segments,
      height,
      subRoofs,
      clipContext,
      thickness,
    });

    const clipBSP = clipPlane
      ? addConvexPlanesToBSPLeaf([planeBSPFromPlane(clipPlane), planeBSPFromPlane(basePlane)])
      : null;

    const options: ClipFaceContext = {
      clippingNodes: [clippingNodeNew(clipBSP, true), clippingNodeNew(discardBSP, true)],
      epsilon: MULTIGABLE_BSP_EPSILON,
    };

    const faces: Face[] = [];
    for (const wallBaseBoardRail of wallBaseBoardRails) {
      const ext = extrudeFaceAlongPolyLine3([profile], wallBaseBoardRail, 0, 0, 1);

      clipFaces({
        faces: ext.faces,
        options,
        clippedFaces: faces,
      });
    }

    if (startCapPlane && wallBaseBoardRails.length > 0) {
      const firstRail = wallBaseBoardRails[0];
      if (firstRail) {
        const [x0, y0, z0] = polyLine3StartPoint(firstRail);
        const [tx, ty, tz] = polyLine3EdgeTangent(firstRail, 0);
        const angle = vec3AngleBetween(tx, ty, tz, tx, ty, 0);
        const startPlane = planeCopyMoved(startCapPlane, x0, y0, z0);
        const capProfile = polyLine2SetCW(polyLine2ScaleY(polyLine2Copy(profile), 1 / Math.cos(angle)));

        faces.push(faceNew([capProfile], startPlane));
      }
    }

    if (endCapPlane && wallBaseBoardRails.length > 0) {
      const lastRail = wallBaseBoardRails[wallBaseBoardRails.length - 1];
      const lastRailEdgeCount = polyLine3EdgeCount(lastRail);
      if (lastRail && lastRailEdgeCount > 0) {
        const [x0, y0, z0] = polyLine3EndPoint(lastRail);
        const [tx, ty, tz] = polyLine3EdgeTangent(lastRail, lastRailEdgeCount - 1);
        const angle = vec3AngleBetween(tx, ty, tz, tx, ty, 0);
        const startPlane = planeCopyMoved(endCapPlane, x0, y0, z0);
        const capProfile = polyLine2SetCW(
          polyLine2ScaleY(polyLine2ReflectOverYAxis(polyLine2Copy(profile)), 1 / Math.cos(angle)),
        );

        faces.push(faceNew([capProfile], startPlane));
      }
    }

    wallBaseBoards.push(faceArrayViewEntityNew(tagFaces(faces, tag), descriptor, "trimExterior0", pickHandle));
  }
}

interface BuildWallBaseBoardRailsResult {
  readonly wallBaseBoardRails: CoplanarRails[];
  readonly rtree: RBush3D;
}

interface BuildWallBaseBoardRailsOptions {
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly roofTopFaces: RoofFace[];
  readonly walls: Table<WallSystem>;
  readonly clipContext: MultiGableRoofClipContext;
}

export function buildWallBaseBoardRails({
  subRoof,
  subRoofs,
  roofTopFaces,
  walls,
  clipContext,
}: BuildWallBaseBoardRailsOptions): BuildWallBaseBoardRailsResult {
  const { roofSettings, roofVolume, wallRoofBaseBoard, wallRoofBaseBoardFilter } = subRoof;
  const { overhangs } = roofVolume;
  const { edgeOverhangs } = overhangs;

  const { insideBSP, overlapBSP } = clipContext;

  const maxOverhang = Math.max(...edgeOverhangs);
  const soffitDepth = getSoffitDepth(roofSettings);

  const groundPlane = planeNewXY();

  const railOptions: ClipRailContext = {
    clippingNodes: [clippingNodeNew(insideBSP, true)],
    coplanarsToFront: false,
    epsilon: WALL_BASE_BOARD_COPLANAR_TOLERANCE,
  };

  // look for roof edges coplanar with walls
  const baseBoardRails: CoplanarRails[] = [];
  for (const { face } of roofTopFaces) {
    const { loops, plane } = face;

    for (const loop of loops) {
      const loop3 = polyLine2OnPlane(plane, loop);
      const edgeCount = loop3 ? polyLine3EdgeCount(loop3) : 0;
      for (let i = 0; i < edgeCount; i++) {
        const faceEdge = polyLine3Edge(loop3, i);
        const [x0, y0] = polyLine3StartPoint(faceEdge);
        const [x1, y1] = polyLine3EndPoint(faceEdge);
        const [, , slope] = polyLine3EdgeTangent(faceEdge, 0);
        const [tx, ty] = polyLine2EdgeTangent(planeProjectPolyLine3(groundPlane, faceEdge), 0);
        const isHorizontalEdge = Math.abs(slope) < HORIZONTAL_WALL_BASE_BOARD_TOLERANCE;

        if (
          (wallRoofBaseBoardFilter === WallRoofBaseBoardFilter.SLOPED_EDGES && isHorizontalEdge) ||
          (wallRoofBaseBoardFilter === WallRoofBaseBoardFilter.HORIZONTAL_EDGES && !isHorizontalEdge)
        ) {
          continue;
        }

        const railPlane = planeByThreePoints(x0, y0, 0, x1, y1, 0, x1, y1, 10);
        const offset = 2 * MULTIGABLE_BSP_EPSILON;

        const rails = clipRail3({
          rail: polyLine3Move(polyLine3Copy(faceEdge), 0, 0, offset),
          options: railOptions,
        }).map((rail) => polyLine3Move(rail, 0, 0, -offset));

        if (rails.length === 0) {
          continue;
        }

        // generate extra rails for the full mode
        if (wallRoofBaseBoard === WallRoofBaseBoardMode.FULL) {
          const [x0, y0, z0] = polyLine3EdgeStart(faceEdge, 0);
          const [x1, y1, z1] = polyLine3EdgeEnd(faceEdge, 0);

          if (
            !isPointInsideBSP(
              overlapBSP,
              x0 - tx * offset,
              y0 - ty * offset,
              z0 - offset,
              false,
              MULTIGABLE_BSP_EPSILON,
            )
          ) {
            clipRail3({
              rail: polyLine3ByTwoPoints(
                x0 + tx * maxOverhang,
                y0 + ty * maxOverhang,
                z0 - soffitDepth,
                x0,
                y0,
                z0 - soffitDepth,
              ),
              options: railOptions,
              clippedRails: rails,
            });

            clipRail3({
              rail: polyLine3ByTwoPoints(x0, y0, z0 - soffitDepth, x0, y0, z0),
              options: railOptions,
              clippedRails: rails,
            });
          }

          if (
            !isPointInsideBSP(
              overlapBSP,
              x1 + tx * offset,
              y1 + ty * offset,
              z1 - offset,
              false,
              MULTIGABLE_BSP_EPSILON,
            )
          ) {
            clipRail3({
              rail: polyLine3ByTwoPoints(x1, y1, z1, x1, y1, z1 - soffitDepth),
              options: railOptions,
              clippedRails: rails,
            });

            clipRail3({
              rail: polyLine3ByTwoPoints(
                x1,
                y1,
                z1 - soffitDepth,
                x1 - tx * maxOverhang,
                y1 - ty * maxOverhang,
                z1 - soffitDepth,
              ),
              options: railOptions,
              clippedRails: rails,
            });
          }
        }

        if (!railPlane) {
          continue;
        }

        const [railPlaneNormalX, railPlaneNormalY, railPlaneNormalZ] = planeNormal(railPlane);
        const [ax, ay, az] = planeProject3(plane, railPlane.originX, railPlane.originY, railPlane.originZ);
        const [bx, by, bz] = planeProject3(
          plane,
          railPlane.originX + railPlaneNormalX,
          railPlane.originY + railPlaneNormalY,
          railPlane.originZ + railPlaneNormalZ,
        );
        const [projDirectionX, projDirectionY, projDirectionZ] = vec3Normalize(bx - ax, by - ay, bz - az);

        baseBoardRails.push(coplanarRailsNew(railPlane, projDirectionX, projDirectionY, projDirectionZ, rails));
      }
    }
  }

  // reprojection
  const reprojectedBaseBoardRails = reprojectCoplanarRailsArrayToWalls({
    coplanarRailsArray: baseBoardRails,
    walls,
    subRoofs,
  });

  // group the rails that share the same plane
  const coplanarBaseBoardRails = groupCoplanarRails(reprojectedBaseBoardRails);

  // join rails from the same group and create a rtree with the resulting rail ends
  const rtree = new RBush3D();
  const coplanarJoinedBaseBoardRails = rejoinCoplanarRails({
    inputRails: coplanarBaseBoardRails,
    rtree,
    boxHalfSize: WALL_BASE_BOARD_RTREE_BOX_HALF_WIDTH,
  });

  return {
    wallBaseBoardRails: coplanarJoinedBaseBoardRails,
    rtree,
  };
}

function buildWallBaseBoardClippingBSPs(
  walls: Table<WallSystem>,
  subRoofs: Table<GeneratedRoof>,
  refPlane: Plane,
  baseBoardThickness: number,
): NodeBSP[] {
  const wallBaseBoardBSPs: NodeBSP[] = [];

  const addFaceToBSP = (face: Face) => {
    const { loops, plane } = face;

    if (
      loops.length === 0 ||
      polyLine2Area(loops[0]) < DIST_EPSILON_SQUARED ||
      !planeIsCoplanar(plane, refPlane, MULTIGABLE_BSP_EPSILON, MULTIGABLE_BSP_EPSILON)
    ) {
      return;
    }

    const plane1 = planeCopyReversed(plane);
    const plane2 = planeMoveAlongNormal(planeCopyReversed(plane1), baseBoardThickness);
    const [nX, nY, nZ] = planeNormal(plane);

    const polygons: PolygonBSP[] = [];
    for (const loop of loops) {
      const pl3 = polyLine2OnPlane(plane, loop);

      for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
        polygons.push(
          buildEdgePolygon(
            pl3[i] + nX * baseBoardThickness,
            pl3[i + 1] + nY * baseBoardThickness,
            pl3[i + 2] + nZ * baseBoardThickness,
            pl3[i + 3] + nX * baseBoardThickness,
            pl3[i + 4] + nY * baseBoardThickness,
            pl3[i + 5] + nZ * baseBoardThickness,
            -nX * baseBoardThickness,
            -nY * baseBoardThickness,
            -nZ * baseBoardThickness,
          ) as PolygonBSP,
        );
      }
    }

    buildPolygonsFromPolyline2(planeReProjectPolyLine2(loops[0], plane, plane1), plane1, 0, polygons);
    buildPolygonsFromPolyline2(planeReProjectPolyLine2(loops[0], plane, plane2), plane2, 0, polygons);

    const bsp = addPolygonsToBSP(null, null, polygons, MULTIGABLE_BSP_EPSILON);

    if (bsp) {
      wallBaseBoardBSPs.push(bsp);
    }
  };

  const addFacesToBSP = (faces: Face[]) => {
    for (const face of faces) {
      addFaceToBSP(face);
    }
  };

  const addWallToBSP = (wall: Wall) => {
    if (wall.wallType !== WallType.SOLID) {
      return;
    }

    if (!isExteriorWallCondition[wall.wallCondition]) {
      return;
    }

    const { wallFaces } = wall;
    const path = getWallPath(wall);
    const { leftRoom, rightRoom } = path;
    const { leftFaces, rightFaces } = wallFaces;

    if (leftRoom?.attributes.roomEnclosure && isInteriorRoomEnclosure[leftRoom?.attributes.roomEnclosure]) {
      addFacesToBSP(rightFaces);
    }

    if (rightRoom?.attributes.roomEnclosure && isInteriorRoomEnclosure[rightRoom?.attributes.roomEnclosure]) {
      addFacesToBSP(leftFaces);
    }
  };

  const addWallSystemToBSP = ({ walls }: WallSystem) => {
    for (const wallId in walls) {
      const wall = walls[wallId];

      addWallToBSP(wall);
    }
  };

  // add normal walls
  for (const wallSystem of Object.values(walls)) {
    addWallSystemToBSP(wallSystem);
  }

  // add roof walls
  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];
    const { roofWallSystem } = subRoof;

    if (roofWallSystem) {
      // new code path for roof wall systems
      addWallSystemToBSP(roofWallSystem.wallSystem);
    } else {
      const { wallFaces } = subRoof;
      const { startWallFaces, endWallFaces } = wallFaces;

      // old code path
      addFacesToBSP(startWallFaces.leftFaces);
      addFacesToBSP(endWallFaces.leftFaces);
    }
  }

  return wallBaseBoardBSPs;
}

interface ProjectDormerSegmentsOptions {
  readonly segments: PolyLine2[];
  readonly height: number;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly clipContext: MultiGableRoofClipContext;
  readonly thickness: number;
}

function projectDormerSegments({
  segments,
  height,
  subRoofs,
  clipContext,
  thickness,
}: ProjectDormerSegmentsOptions): PolyLine3[] {
  const { discardNoDormersBSP } = clipContext;

  interface RBox {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    line: PolyLine2;
  }

  const rtree = new RBush<RBox>();

  // helper functions
  const addSplitLine = (line: PolyLine2) => {
    const aabb = aabb2Grow(polyLine2AABB2(line), DIST_EPSILON, DIST_EPSILON);
    rtree.insert({
      ...aabb,
      line,
    });
  };

  const getRoofHeightAtPoint = (x: number, y: number): number => {
    const int = rayIntersectionBSP({
      node: discardNoDormersBSP,
      x1: x,
      y1: y,
      z1: height + 1,
      x2: x,
      y2: y,
      z2: 0,
    });

    return int?.entryPoint?.z ?? -1;
  };

  const addSubRoofSplitLines = ({ roofTopFaces }: GeneratedRoof) => {
    for (const { face } of roofTopFaces) {
      const { loops, plane } = face;

      if (!loops?.length || !plane) {
        continue;
      }

      const pl = polyLine3OnPlane(loops[0], plane);
      const edgeCount = pl ? polyLine3EdgeCount(pl) : 0;
      for (let j = 0; j < edgeCount; j++) {
        const [x0, y0] = polyLine3EdgeStart(pl, j);
        const [x1, y1] = polyLine3EdgeEnd(pl, j);

        addSplitLine(polyLine2ByTwoPoints(x0, y0, x1, y1));
      }
    }
  };

  // 1.- build an RTree with the potential split lines
  for (const subRoofId in subRoofs) {
    addSubRoofSplitLines(subRoofs[subRoofId]);
  }

  // 2.- Split the perimeters with the inserted lines
  const projectedPerimeters: PolyLine3[] = [];
  for (let segment of segments) {
    const points: number[] = [];
    let validSegment = true;

    segment = offsetPolyLine2(segment, -thickness);

    for (let i = 0, l = polyLine2EdgeCount(segment); i < l; i++) {
      const [x0, y0] = polyLine2EdgeStart(segment, i);
      const [x1, y1] = polyLine2EdgeEnd(segment, i);

      // start point
      let z = getRoofHeightAtPoint(x0, y0);
      validSegment = validSegment && z > 0;
      pushXYZ(points, x0, y0, z);

      // look for intersections using the RTree
      const intersectionPoints: number[][] = [];
      const searchAABB = aabb2ByTwoPoints(x0, y0, x1, y1);
      const splitLines = rtree.search(searchAABB);

      for (let j = 0, lj = splitLines.length; j < lj; j++) {
        const { line } = splitLines[j];
        const [ax, ay] = polyLine2EdgeStart(line, 0);
        const [bx, by] = polyLine2EdgeEnd(line, 0);

        const int = intersectLine2Line2(x0, y0, x1, y1, ax, ay, bx, by);

        if (int && int instanceof CurveCurvePointIntersection) {
          intersectionPoints.push([int.x, int.y, vec2Dot(int.x - x0, int.y - y0, x1 - x0, y1 - y0)]);
        }
      }

      // sort the intersection (by distance to the start point)
      intersectionPoints.sort((a: number[], b: number[]) => a[2] - b[2]);

      // insert the sorted intersections
      for (const intersectionPoint of intersectionPoints) {
        z = getRoofHeightAtPoint(intersectionPoint[0], intersectionPoint[1]);
        validSegment = validSegment && z > 0;

        pushXYZ(
          points,
          intersectionPoint[0],
          intersectionPoint[1],
          getRoofHeightAtPoint(intersectionPoint[0], intersectionPoint[1]),
        );
      }

      // end point
      z = getRoofHeightAtPoint(x1, y1);
      validSegment = validSegment && z > 0;
      pushXYZ(points, x1, y1, z);
    }

    // Build the final polyline
    if (validSegment) {
      projectedPerimeters.push(offsetPolyLine3InXYPlane(polyLine3Simplify(polyLine3New(points)), thickness));
    }
  }

  return projectedPerimeters;
}
